import React from 'react'
import { useIntl } from 'react-intl'
import { useConfiguration } from 'contexts/configuration'
import { useEntitiesClientState, useEntities } from 'state/entities'
import { getDateRangeDefault } from 'pages/activity-log/utilities'
import { subYears, subQuarters, subMonths } from 'date-fns'
import { getYearRange, getQuarterRange, getMonthRange } from 'pages/absence/utilities'
import { compact } from 'utilities/array'
import Filter from 'components/filter'

const ActivityLogFilter = ({ salt }) => {
    const { formatMessage } = useIntl()

    const { configuration } = useConfiguration()

    const {
        filter,
        setFilter
    } = useEntitiesClientState(state => ({
        filter: state.filter,
        setFilter: state.setFilter
    }))

    const {
        items,
        isFetching,
        isFetched
    } = useEntities()

    const now = new Date()

    const advancedFields = compact([
        {
            type: 'time',
            name: 'dateRange',
            field: {
                defaultValue: getDateRangeDefault(),
                range: true,
                unsettable: false
            },
            controlProps: {
                placeholder: formatMessage({
                    id: 'noun_date',
                    defaultMessage: 'Date'
                })
            },
            picker: {
                future: false,
                label: formatMessage({
                    id: 'time_action_date_pick',
                    defaultMessage: 'Pick date'
                }),
                shortcuts: [
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_year_previous',
                            defaultMessage: 'Last year'
                        }),
                        range: getYearRange({ date: subYears(now, 1) })
                    },
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_year_current',
                            defaultMessage: 'This year'
                        }),
                        range: getYearRange({ limitToToday: true })
                    },
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_quarter_previous',
                            defaultMessage: 'Last quarter'
                        }),
                        range: getQuarterRange({ date: subQuarters(now, 1) })
                    },
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_quarter_current',
                            defaultMessage: 'This quarter'
                        }),
                        range: getQuarterRange({ limitToToday: true })
                    },
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_month_previous',
                            defaultMessage: 'Last month'
                        }),
                        range: getMonthRange({ date: subMonths(now, 1) })
                    },
                    {
                        label: formatMessage({
                            id: 'date_range_predefined_month_current',
                            defaultMessage: 'This month'
                        }),
                        range: getMonthRange({ limitToToday: true })
                    }
                ]
            }
        },
        !!configuration?.enums?.activityLogResourceType && {
            type: 'checkboxes',
            name: 'resourceType',
            label: formatMessage({
                id: 'noun_resource_type',
                defaultMessage: 'Resource type'
            }),
            empty: null,
            getValueLabel: value => humanize(value),
            field: {
                defaultValue: null,
                resettable: true,
                options: configuration?.enums.activityLogResourceType.map(resourceType => ({
                    id: resourceType,
                    value: resourceType,
                    name: humanize(resourceType)
                }))
            }
        },
        !!configuration?.enums?.activityLogCategory && {
            type: 'checkboxes',
            name: 'category',
            label: formatMessage({
                id: 'noun_category',
                defaultMessage: 'Category'
            }),
            empty: null,
            getValueLabel: value => humanize(value),
            field: {
                defaultValue: null,
                resettable: true,
                options: configuration?.enums.activityLogCategory.map(category => ({
                    id: category,
                    value: category,
                    name: humanize(category)
                }))
            }
        },
        {
            type: 'shareUser',
            name: 'actorId',
            label: formatMessage({
                id: 'noun_actor',
                defaultMessage: 'Actor'
            }),
            empty: null,
            field: {
                defaultValue: null
            },
            quickSelectMe: true
        },
        {
            type: 'shareUser',
            name: 'concernsId',
            label: formatMessage({
                id: 'person_label_concerns',
                defaultMessage: 'Concerns'
            }),
            empty: null,
            field: {
                defaultValue: null
            },
            quickSelectMe: true
        }
    ])

    return (
        <Filter
            filter={filter}
            setFilter={setFilter}
            advancedFields={advancedFields}
            items={items}
            fetching={isFetching}
            hasFetched={isFetched}
            salt={salt} />
    )
}

const humanize = value => {
    if(typeof value !== 'string') {
        return value
    }

    const [first, ...rest] = value.toLowerCase().split('_')
    return `${first[0].toUpperCase()}${first.slice(1)} ${rest.join(' ')}`
}

export default ActivityLogFilter